/* eslint-disable class-methods-use-this */
import React from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { DemographicAppKeys } from '../../../../../global/models/demographics-model';
import { TcsmEpiApiState } from '../../../../../root/tcsm-model';
import { setAEStep } from '../../../../annual-enrollment/annual-enrollment-slice';
import { AESteps, AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { NewHireEnrollmentRoutes } from '../../../../arc/features/new-hire-enrollment/models/new-hire-routes';
import { BenefitEnrollmentStep } from '../../../../arc/features/new-hire-enrollment/models/pension-enrollment-models';
import { AccountInfoRoutes } from '../../../constants/routes';
import { setCurrentMyCpgMidYearStep } from '../../mycpg-midyear/my-cpg-mid-year-slice';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { MyCpgMidYearPersonalInfoAlertTypes } from '../../../../notification/notification-models';

type BannerStatus = 'success' | 'error' | 'info' | 'warning';

export class PersonalInfoWizard {

	readonly epiContent: TcsmEpiApiState;

	readonly enrollmentContext: AnnualEnrollmentKey;

	readonly app: DemographicAppKeys;

	readonly dispatch: any;

	readonly history: any;

	readonly alerts: MyCpgMidYearPersonalInfoAlertTypes[];

	constructor(epiContent: TcsmEpiApiState, enrollmentContext: AnnualEnrollmentKey, app: DemographicAppKeys, dispatch: any, history: any, alerts?: MyCpgMidYearPersonalInfoAlertTypes[]) {
		this.epiContent = epiContent;
		this.enrollmentContext = enrollmentContext;
		this.app = app;
		this.dispatch = dispatch;
		this.history = history;
		this.alerts = alerts;
	}

	public getPageDescription(): string {
		if (this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment && (this.app === DemographicAppKeys.myCPG)) {
			return this.epiContent?.annualEnrollmentSettingsState?.epiContent?.personalInformationStepDescription;
		}

		if (this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment && (this.app === DemographicAppKeys.arc)) {
			return this.epiContent?.annualEnrollmentSettingsState?.epiContent?.personalInformationStepDescriptionMAP || this.epiContent?.annualEnrollmentSettingsState?.epiContent?.personalInformationStepDescription;
		}
		if (this.enrollmentContext === AnnualEnrollmentKey.participantEnrollment) {
			return this.epiContent?.participantEnrollmentSettingsState?.epiContent?.personalInformationDescription;
		}
		return this.epiContent?.personalInfoSettingState?.epiContent?.pageIntro;
	}

	public getPageHeader(): string {
		return this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment ? this.epiContent?.annualEnrollmentSettingsState?.epiContent?.personalInformationStepTitle : this.epiContent?.personalInfoSettingState?.epiContent?.pageTitle;
	}

	public useEffectDispatch(): void {
		if (this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment) {
			this.dispatch(setAEStep(AESteps.PersonalInfo, this.app));
		}
		if (this.enrollmentContext === AnnualEnrollmentKey.mycpgmidyear) {
			this.dispatch(setCurrentMyCpgMidYearStep(BenefitEnrollmentStep.PERSONAL_INFORMATION));
		}
	}

	public onSuccessRedirect(basePath: string): void {
		if (this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment || this.enrollmentContext === AnnualEnrollmentKey.participantEnrollment || this.enrollmentContext === AnnualEnrollmentKey.mycpgmidyear) {
			this.history.push(`${basePath}${AccountInfoRoutes.RelationshipsSegment}`);
		}
	}

	public onCancelRedirect(basePath: string, isDioApprover?: boolean): void {
		if (isDioApprover) {
			this.history.push(`${basePath}${NewHireEnrollmentRoutes.Coverage}`);
		}

		if (this.enrollmentContext === AnnualEnrollmentKey.newHireEnrollment && !isDioApprover) {
			this.history.push(`${(basePath.replace('/benefit-enrollment', NewHireEnrollmentRoutes.WhoWillEnroll))}`);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public onSuccessNoChanges(basePath: string): void {
	}

	public isEnrollmentForm(): boolean {
		return this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment || this.enrollmentContext === AnnualEnrollmentKey.participantEnrollment || this.enrollmentContext === AnnualEnrollmentKey.mycpgmidyear;
	}

	public isAnnualEnrollmentForm(): boolean {
		return this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment;
	}

	public getGpsType(): GpsType {
		switch (this.enrollmentContext) {
			case AnnualEnrollmentKey.annualEnrollment:
				return this.enrollmentContext === AnnualEnrollmentKey.annualEnrollment ? GpsType.AE_DEMOGRAPHIC : GpsType.DEMOGRAPHIC;
			case AnnualEnrollmentKey.participantEnrollment:
				return GpsType.NEW_PARTICIPANT_BENEFIT_PERSON_INFO;

			default:
				return GpsType.DEMOGRAPHIC;
		}
	}

	public getSubmitButtonText() {
		return this.isEnrollmentForm() ? this.epiContent?.annualEnrollmentSettingsState?.epiContent?.personalInformationNextButtonText : this.epiContent?.labelSettingState.epiContent?.submitText;
	}

	public getCancelButtonText() {
		return this.isEnrollmentForm() ? this.epiContent?.annualEnrollmentSettingsState?.epiContent.personalInformationCancelButtonText : this.epiContent?.labelSettingState.epiContent?.cancelText;
	}

	public hasMaritalStatusSection(): boolean {
		return !this.isEnrollmentForm();
	}

	public preventButtonDisableValidation(): boolean {
		return this.isEnrollmentForm();
	}

	public renderAlerts(): JSX.Element {
		if (this.alerts?.length > 0) {

			const match = this.alerts?.map(alert => this.epiContent?.myCpgMidyearSettingsState?.epiContent?.myCpgMidYearAlertsMapping?.find(a => a?.alertKey === alert));

			return (
				<ContentSpacer>
					{
						match?.map(alert => <ContentSpacer>
							<Banner
								banner={{
									type: alert?.alertType as unknown as BannerStatus,
									label: alert?.alertTitle,
									description: <WysiwygWithReactLink content={alert?.alertDescription} />,
								}}
							/>

						</ContentSpacer>)
					}
				</ContentSpacer>
			);
		}

		return null;
	}
}